import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import Sidebar from './Sidebar';
import Modal from '../../components/Modal';
import { Cpu, Menu, CheckCircle, FileText, Users, Layers } from 'lucide-react';
import martechLogo from '../../assets/Asset 2.png';
import { PageLoader } from '../../components/PageLoader';
import AiAssistantWidget from '../ai_assistant/AiAssistantWidget';
import { AICommandProvider, useAICommand } from '../../context/AICommandContext';
import ForcePasswordChangeModal from '../auth/ForcePasswordChangeModal';


// Import all view components
import HomeView from '../home/HomeView';
import FlightDeckView from '../flightdeck/FlightDeckView';
import CampaignsView from '../campaigns/CampaignsView';
import AnalyticsView from '../analytics/AnalyticsView';
import CompassView from '../compass/CompassView';
import TeamView from '../team/TeamView';
import IntegrationsView from '../integrations/IntegrationsView';
import DataManagementView from '../datamanagement/DataManagementView';
import SettingsView from '../settings/SettingsView';
import AdminView from '../admin/AdminView';
import SeoAuditView from '../seo/SeoAuditView';
import FoundryDashboardView from '../foundry/FoundryDashboardView';
import ContentCreationView from '../foundry/ContentCreationView';
import EmailBuilderView from '../foundry/EmailBuilderView';
import HubSpotQueryTester from '../integrations/HubSpotQueryTester';
import CompetitorAnalysisView from '../foundry/CompetitorAnalysisView';
import InsightsView from '../insights/InsightsView'; // <-- NEW IMPORT

const initialFoundryAssets = {
    published: [], 
    drafts: []      
};

const Toast = ({ message, onDone }) => {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                onDone();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [message, onDone]);

    if (!message) return null;

    return (
        <div className="fixed top-5 right-5 bg-teal-500 text-white py-3 px-6 rounded-lg shadow-lg z-50 animate-fade-in-down">
            <div className="flex items-center gap-3">
                <CheckCircle size={20} />
                <span className="font-semibold">{message}</span>
            </div>
        </div>
    );
};


const MainLayoutContent = () => {
    const { user, refetchUser } = useAuth();
    const { command, clearCommand } = useAICommand();
    const [activeView, setActiveView] = useState('home');
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [workspaceVersion, setWorkspaceVersion] = useState(1);
    const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [foundryAssets, setFoundryAssets] = useState(initialFoundryAssets);
    const [flightDeckTasks, setFlightDeckTasks] = useState([]);
    const [isTasksLoading, setIsTasksLoading] = useState(true);
    const [editingDraft, setEditingDraft] = useState(null);
    const needsPasswordChange = user?.passwordChangeRequired === true;


    const activeWorkspace = useMemo(() => {
        if (!user || !user.activeWorkspace || !user.account?.workspaces) return null;
        return user.account.workspaces.find(ws => ws._id.toString() === user.activeWorkspace.toString());
    }, [user]);

    useEffect(() => {
        if (user && activeWorkspace) {
            setIsTasksLoading(true);
            apiService.fetchFlightDeckTasks()
                .then(data => {
                    setFlightDeckTasks(data || []);
                    setIsTasksLoading(false);
                })
                .catch(err => {
                    console.error("Failed to fetch Flight Deck tasks:", err);
                    setIsTasksLoading(false);
                });
        }
    }, [user, activeWorkspace]);

    useEffect(() => {
        const handleConnectionCallback = async () => {
            const params = new URLSearchParams(window.location.search);
            const connectedService = params.get('connected');
            
            if (connectedService) {
                window.history.replaceState({}, document.title, window.location.pathname);
                setIsConnecting(true);
                await refetchUser();
                setActiveView('integrations');
                const serviceName = connectedService.charAt(0).toUpperCase() + connectedService.slice(1);
                setToastMessage(`${serviceName} connected successfully!`);
                setIsConnecting(false);
            }
        };

        handleConnectionCallback();
    }, [refetchUser]);


    const handleCreateWorkspace = async (name) => {
        try {
            await apiService.createWorkspace(name);
            await refetchUser();
            setWorkspaceVersion(v => v + 1);
            setIsWorkspaceModalOpen(false);
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };

    const handleSwitchWorkspace = async (workspaceId) => {
        try {
            await apiService.switchWorkspace(workspaceId);
            await refetchUser();
            setWorkspaceVersion(v => v + 1);
        } catch (error) {
            alert(`Error: ${error.message}`);
        }
    };
    
    const handleCreateTask = async (taskData) => {
        try {
            const newTask = await apiService.createFlightDeckTask(taskData);
            setFlightDeckTasks(prevTasks => [newTask, ...prevTasks]);
            setToastMessage('Task successfully sent to Flight Deck!');
        } catch (err) {
            setToastMessage(`Error: ${err.message}`);
        }
    };

    const handleUpdateTask = (taskId, updateData) => {
        apiService.updateFlightDeckTask(taskId, updateData).then(updatedTaskFields => {
            setFlightDeckTasks(prevTasks => prevTasks.map(t =>
                t._id === taskId ? { ...t, ...updatedTaskFields } : t
            ));
        });
    };

    const handleDeleteTask = (taskId) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            apiService.deleteFlightDeckTask(taskId).then(() => {
                setFlightDeckTasks(prevTasks => prevTasks.filter(t => t._id !== taskId));
            });
        }
    };

    const renderActiveView = () => {
        if (!activeWorkspace) return <PageLoader message="Loading workspace..." />;
        
        const props = {
            key: `${activeWorkspace?._id}-${workspaceVersion}`,
            user,
            activeWorkspace,
            setActiveView,
            aiCommand: command,
            clearAiCommand: clearCommand,
            foundryAssets,
            setFoundryAssets,
        };
        
        switch (activeView) {
            case 'home': return <HomeView {...props} onCreateTask={handleCreateTask} />;
            case 'flightdeck': return <FlightDeckView {...props} tasks={flightDeckTasks} isLoading={isTasksLoading} onUpdateTask={handleUpdateTask} onDeleteTask={handleDeleteTask} />;
            case 'campaigns': return <CampaignsView {...props} />;
            case 'foundry': return <FoundryDashboardView {...props} activeView={activeView} setEditingDraft={setEditingDraft} />;
            case 'foundryContentCreation': return <ContentCreationView {...props} activeView={activeView} editingDraft={editingDraft} setEditingDraft={setEditingDraft} />;
            case 'foundryCompetitorAnalysis': return <CompetitorAnalysisView {...props} />;
            case 'foundryEmailDesigner': return <EmailBuilderView {...props} />;
            case 'seoAudit': return <SeoAuditView {...props} onCreateTask={handleCreateTask} />;
            case 'insights': return <InsightsView {...props} />; // <-- NEW CASE
            case 'analytics': return <AnalyticsView {...props} />;
            case 'compass': return <CompassView {...props} />;
            case 'team': return <TeamView {...props} />;
            case 'integrations': return <IntegrationsView {...props} />;
            case 'hubspotQueryTester': return <HubSpotQueryTester {...props} />;
            case 'dataHub': return <DataManagementView {...props} />;
            case 'settings': return <SettingsView {...props} />;
            case 'admin': return <AdminView {...props} />;
            default: return <HomeView {...props} onCreateTask={handleCreateTask} />;
        }
    };
    
    if (isConnecting) {
        return <PageLoader text="Finalizing connection, please wait..." />;
    }

    return (
        <div className="bg-slate-900 text-slate-200 min-h-screen flex font-sans">
             <style>{`
                /* ... CSS Styles ... */
            `}</style>

            <Toast message={toastMessage} onDone={() => setToastMessage('')} />

            <Sidebar
                user={user}
                activeView={activeView}
                setActiveView={setActiveView}
                isExpanded={isSidebarExpanded}
                setIsExpanded={setIsSidebarExpanded}
                isMobileNavOpen={isMobileNavOpen}
                onMobileNavClose={() => setIsMobileNavOpen(false)}
                workspaces={user?.account?.workspaces || []}
                activeWorkspace={activeWorkspace}
                onCreateWorkspace={() => setIsWorkspaceModalOpen(true)}
                onSwitchWorkspace={handleSwitchWorkspace}
            />
            <WorkspaceCreatorModal
                isOpen={isWorkspaceModalOpen}
                onClose={() => setIsWorkspaceModalOpen(false)}
                onSubmit={handleCreateWorkspace}
            />
            
            <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarExpanded ? 'md:ml-64' : 'md:ml-20'}`}>
                <header className="md:hidden p-4 bg-[#0A142C]/80 backdrop-blur-sm sticky top-0 z-20 flex items-center justify-between border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <img src={martechLogo} alt="MartechAI Logo" className="w-8 h-8" />
                        <span className="text-lg font-bold">{activeWorkspace?.name || 'Menu'}</span>
                    </div>
                    <button onClick={() => setIsMobileNavOpen(true)}>
                        <Menu className="w-6 h-6" />
                    </button>
                </header>
                <main className="flex-grow overflow-y-auto p-6">
                    {renderActiveView()}
                </main>
            </div>

            <AiAssistantWidget setActiveView={setActiveView} />
            <ForcePasswordChangeModal isOpen={needsPasswordChange} />
        </div>
    );
};

const MainLayout = () => (
    <AICommandProvider>
        <MainLayoutContent />
    </AICommandProvider>
);

const WorkspaceCreatorModal = ({ isOpen, onClose, onSubmit }) => {
    const [name, setName] = useState('');
    const handleSubmit = (e) => {
        e.preventDefault();
        if (name.trim()) {
            onSubmit(name.trim());
            setName('');
        }
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create New Workspace">
            <form onSubmit={handleSubmit} className="space-y-4">
                 <div>
                    <label htmlFor="workspaceName" className="block mb-2 text-sm font-medium text-slate-300">Workspace Name</label>
                    <input
                        type="text"
                        id="workspaceName"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5"
                        placeholder="e.g. Client Project Q3"
                    />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                    <button type="button" onClick={onClose} className="bg-slate-600 text-white font-bold py-2 px-4 rounded-md hover:bg-slate-500">Cancel</button>
                    <button type="submit" className="bg-purple-600 text-white font-bold py-2 px-4 rounded-md hover:bg-purple-700">Create</button>
                </div>
            </form>
        </Modal>
    );
};

export default MainLayout;